<template>
  <div class="speaking-test">
    <section v-if="phase === 'intro'" class="test-intro">
      <header class="intro-heading">
        <h1 class="intro-title">{{ $t('levelTest.speakingName') }}</h1>
        <span class="intro-meta">{{ $t('levelTest.speakingMeta') }}</span>
      </header>
      <p class="intro-text">{{ $t('levelTest.speakingIntro') }}</p>
      <p class="test-range">{{ $t('levelTest.speakingRange') }}</p>

      <template v-if="!user">
        <p class="notice">{{ $t('levelTest.loginRequired') }}</p>
        <button type="button" class="primary-button" @click="showLoginModal">{{ $t('levelTest.login') }}</button>
      </template>
      <template v-else>
        <p v-if="micError" role="alert" class="notice is-error">{{ micError }}</p>
        <div class="intro-actions">
          <button type="button" class="primary-button" :disabled="!status || starting" :aria-describedby="status ? 'speaking-test-cost' : undefined" @click="start">
            {{ $t(starting ? 'levelTest.startingTest' : 'levelTest.start') }}
          </button>
          <p v-if="status" id="speaking-test-cost" class="intro-cost">{{ status.speakingUsagePoints ? $t('levelTest.speakingCost', { n: status.speakingUsagePoints }) : $t('levelTest.speakingFirstFree') }}</p>
          <button v-if="status?.lastSpeaking" type="button" class="text-button" @click="showLastResult">{{ $t('levelTest.viewReport') }}</button>
        </div>
      </template>
    </section>

    <section v-else-if="phase === 'running'" class="test-task">
      <div class="task-progress" aria-hidden="true">
        <span v-for="(task, index) in tasks" :key="index" :class="['progress-segment', { done: index <= taskIndex }]"></span>
      </div>
      <p class="task-kind">
        {{ $t(task.kind === 'read' ? 'levelTest.taskRead' : 'levelTest.taskAnswer') }}
        <span class="task-count">{{ $t('levelTest.taskProgress', { n: taskIndex + 1, total: tasks.length }) }}</span>
      </p>
      <p class="task-hint">{{ $t(task.kind === 'read' ? 'levelTest.readHint' : 'levelTest.answerHint') }}</p>
      <p class="task-text" lang="en">{{ task.text }}</p>

      <div class="recorder">
        <div v-if="taskPhase === 'prep'" class="preparation" role="timer" :aria-label="$t('levelTest.prepare', { n: countdown })">
          <strong class="countdown">{{ countdown }}</strong>
          <span>{{ $t('levelTest.getReady') }}</span>
        </div>
        <SoundCreature :state="creatureState" :label="creatureLabel" :level="micLevel" :bars="Array(10).fill(micLevel)" />
        <strong v-if="taskPhase === 'prep' || taskPhase === 'recording'" class="creature-label" aria-live="polite">{{ creatureLabel }}</strong>
        <p v-if="taskPhase === 'recording'" class="recorder-status" aria-live="polite">{{ $t('levelTest.recording', { n: countdown }) }}</p>
        <AssessmentProgress v-if="taskPhase === 'connecting' || taskPhase === 'processing'" />
        <button v-if="taskPhase === 'prep' && task.kind === 'answer'" type="button" class="primary-button" @click="beginRecording">
          {{ $t('levelTest.startAnswer') }}
        </button>
        <button v-else type="button" class="primary-button" :disabled="taskPhase !== 'recording'" @click="stopTask">
          {{ $t('levelTest.done') }}
        </button>
      </div>
      <p v-if="retryNotice" class="notice">{{ $t('levelTest.retryTask') }}</p>
    </section>

    <section v-else-if="phase === 'failed'" class="test-scoring">
      <p class="notice is-error">{{ failure }}</p>
      <button type="button" class="primary-button" @click="restart">{{ $t('levelTest.restart') }}</button>
    </section>

    <section v-else-if="phase === 'scoring'" class="test-scoring">
      <AssessmentProgress />
    </section>

    <LevelResult v-else skill="speaking" :level="result.level" @retake="restart">
      <dl class="metrics">
        <div class="metric">
          <dt>{{ $t('levelTest.content') }}</dt>
          <dd>{{ result.contentLevel }}</dd>
        </div>
        <div class="metric">
          <dt>{{ $t('levelTest.pronunciation') }}</dt>
          <dd>{{ result.pronunciation }}</dd>
        </div>
        <div class="metric">
          <dt>{{ $t('levelTest.accuracy') }}</dt>
          <dd>{{ result.accuracy ?? '—' }}</dd>
        </div>
        <div class="metric">
          <dt>{{ $t('levelTest.fluency') }}</dt>
          <dd>{{ result.fluency ?? '—' }}</dd>
        </div>
      </dl>
      <p v-if="result.reason" class="reason">{{ result.reason }}</p>
      <p v-if="result.cappedByPronunciation" class="note">{{ $t('levelTest.cappedNote') }}</p>
      <AssessmentDetails><SpeakingReview :result="result" /></AssessmentDetails>
    </LevelResult>
  </div>
</template>

<script>
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { apiPost } from '@/composables/useApi'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import { useLevelTestRecorder } from '@/composables/useLevelTestRecorder'
import LevelResult from './LevelResult.vue'
import SpeakingReview from './SpeakingReview.vue'
import AssessmentDetails from './AssessmentDetails.vue'
import SoundCreature from '../player/SoundCreature.vue'
import AssessmentProgress from './AssessmentProgress.vue'
import { PREP_SECONDS, ANSWER_PREP_SECONDS, buildSpeakingTasks } from './speakingPrompts'

const READ_ALOUD_COUNT = 3

export default {
  name: 'SpeakingTest',
  components: { LevelResult, SpeakingReview, AssessmentDetails, SoundCreature, AssessmentProgress },
  setup() {
    const { user } = useAuth()
    const { showLoginModal } = useModals()
    const { completeOnboardingTest } = useLearnerProfile()
    const recorder = useLevelTestRecorder()
    return { completeOnboardingTest, user, showLoginModal, recorder, micLevel: recorder.level, speechDetected: recorder.speechDetected, recognitionError: recorder.recognitionError }
  },
  data() {
    return {
      phase: 'intro',
      status: null,
      starting: false,
      requestId: '',
      micError: '',
      tasks: [],
      taskIndex: 0,
      taskPhase: 'prep',
      countdown: 0,
      ticker: null,
      recording: null,
      summaries: [],
      result: null,
      failure: '',
      retryNotice: false
    }
  },
  computed: {
    task() {
      return this.tasks[this.taskIndex]
    },
    creatureState() {
      if (this.taskPhase === 'prep') return 'idle'
      if (this.taskPhase !== 'recording') return 'paused'
      return this.speechDetected ? 'speaking' : 'ready'
    },
    creatureLabel() {
      if (this.taskPhase === 'prep') return this.$t(this.task.kind === 'read' ? 'levelTest.prepareRead' : 'levelTest.prepareAnswer')
      if (this.taskPhase !== 'recording') return this.$t('levelTest.processing')
      return this.$t(this.speechDetected ? 'levelTest.listeningToYou' : this.task.kind === 'read' ? 'levelTest.speakRead' : 'levelTest.speakAnswer')
    }
  },
  watch: {
    // 识别一出错就停下这一题：额度用完换备用资源重来这一题，其他错误结束测试，不提交
    async recognitionError(error) {
      // 已经点了“说完了”的题由 stopTask 收尾，这里只管录音中途出错
      if (!error || this.phase !== 'running' || this.taskPhase !== 'recording') return
      clearInterval(this.ticker)
      const recording = this.recording
      this.recording = null
      await recording?.cancel()
      if (error.quota && this.recorder.switchToFallback()) {
        this.retryNotice = true
        this.runTask()
        return
      }
      this.fail(this.$t('levelTest.recognitionFailed'))
    },
    user: {
      immediate: true,
      handler(user) {
        if (user) this.loadStatus()
      }
    }
  },
  async beforeUnmount() {
    clearInterval(this.ticker)
    this.phase = 'closed'
    await this.recording?.cancel()
    await this.recorder.closeMic()
  },
  methods: {
    showLastResult() {
      this.result = this.status.lastSpeaking
      this.phase = 'result'
    },
    async loadStatus() {
      this.status = await apiPost('/api/level-test/status', {})
    },
    async fail(message) {
      clearInterval(this.ticker)
      this.failure = message
      this.phase = 'failed'
      const recording = this.recording
      this.recording = null
      try { await recording?.cancel() } finally { await this.recorder.closeMic() }
    },
    restart() {
      this.phase = 'intro'
      this.loadStatus()
    },
    async start() {
      if (this.starting || !this.user) return
      this.starting = true
      this.micError = ''
      this.recognitionError = null
      try {
        await this.loadStatus()
        if (this.status.remainingUsage < this.status.speakingUsagePoints) {
          this.micError = this.$t('levelTest.speakingInsufficientUsage')
          this.starting = false
          return
        }
        await this.recorder.openMic()
      } catch (error) {
        this.micError = this.$t('levelTest.micDenied')
        this.starting = false
        return
      }
      this.starting = false
      if (this.phase === 'closed') { await this.recorder.closeMic(); return }
      this.tasks = buildSpeakingTasks(Math.random, undefined, this.user.uid)
      this.requestId = crypto.randomUUID()
      this.taskIndex = 0
      this.summaries = []
      this.phase = 'running'
      this.runTask()
    },
    countDown(seconds, onDone) {
      clearInterval(this.ticker)
      this.countdown = seconds
      this.ticker = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          clearInterval(this.ticker)
          onDone()
        }
      }, 1000)
    },
    runTask() {
      this.taskPhase = 'prep'
      this.countDown(this.task.kind === 'answer' ? ANSWER_PREP_SECONDS : PREP_SECONDS, this.beginRecording)
    },
    async beginRecording() {
      if (this.phase !== 'running' || this.taskPhase !== 'prep') return
      clearInterval(this.ticker)
      this.taskPhase = 'connecting'
      try {
        this.recording = await this.recorder.startRecording(this.task.kind === 'read' ? this.task.text : '')
        if (this.phase !== 'running') { await this.recording.cancel(); return }
        this.taskPhase = 'recording'
        this.countDown(this.task.seconds, this.stopTask)
      } catch { this.fail(this.$t('levelTest.recognitionFailed')) }
    },
    async stopTask() {
      if (this.taskPhase !== 'recording') return
      clearInterval(this.ticker)
      this.taskPhase = 'processing'
      const recording = this.recording
      this.recording = null
      try { this.summaries.push(await recording.finish()) }
      catch { await this.fail(this.$t('levelTest.recognitionFailed')); return }
      if (this.phase !== 'running') return
      this.retryNotice = false
      if (this.taskIndex < this.tasks.length - 1) {
        this.taskIndex += 1
        this.runTask()
        return
      }
      await this.recorder.closeMic()
      if (this.phase === 'running') this.submit()
    },
    async submit() {
      this.phase = 'scoring'
      const answers = this.summaries.slice(READ_ALOUD_COUNT).map((summary, index) => ({
        question: this.tasks[READ_ALOUD_COUNT + index].text,
        transcript: summary.transcript,
        wordsPerMinute: summary.wordsPerMinute,
        longPauses: summary.longPauses,
        fluency: summary.fluency,
        accuracy: summary.accuracy,
        speechDurationSeconds: summary.speechDurationSeconds
      }))
      try {
        const response = await apiPost('/api/level-test/speaking', {
          requestId: this.requestId,
          readAloud: this.summaries.slice(0, READ_ALOUD_COUNT).map((summary, index) => ({
            text: this.tasks[index].text,
            transcript: summary.transcript,
            pronunciation: summary.pronunciation,
            accuracy: summary.accuracy,
            fluency: summary.fluency
          })),
          answers,
          language: this.$i18n.locale === 'zh' ? 'zh' : 'en'
        })
        this.result = response.result
        this.completeOnboardingTest('speaking', this.result.level)
        this.phase = 'result'
      } catch (error) {
        this.fail(this.$t(error.code === 'NO_SPEECH' ? 'levelTest.noSpeech' : error.code === 'INSUFFICIENT_USAGE' ? 'levelTest.speakingInsufficientUsage' : 'levelTest.scoreFailed'))
      }
    }
  }
}
</script>

<style scoped>
.test-range { margin: var(--space-3) 0 var(--space-5); color: var(--ink-secondary); font-size: var(--font-size-sm); line-height: 1.6; }

.speaking-test {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-xl);
  background: var(--surface-panel);
}

.test-intro {
  padding: var(--space-8);
}
.intro-heading { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); }
.intro-actions { display: grid; grid-template-columns: auto 1fr auto; gap: var(--space-3); align-items: center; }
.intro-cost { margin: 0; color: var(--ink-secondary); font-size: var(--font-size-sm); line-height: 1.5; }

.intro-title {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.intro-meta {
  flex-shrink: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.intro-text {
  max-width: 34rem;
  margin: var(--space-3) 0 var(--space-6);
  color: var(--ink-secondary);
  font-size: var(--font-size-md);
  line-height: 1.6;
}

.notice {
  margin: 0 0 var(--space-4);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.notice.is-error {
  color: var(--color-danger);
}

.last-result {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.last-result strong {
  margin: 0 var(--space-1);
  color: var(--ink-primary);
  font-family: var(--font-family-data);
}

.test-task,
.test-scoring {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-8) var(--space-6);
  text-align: center;
}

.task-progress {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: var(--space-1);
  width: 100%;
  max-width: 240px;
}

.progress-segment {
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
}

.progress-segment.done {
  background: var(--signal-active-strong);
}

.task-kind {
  margin: var(--space-6) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  font-weight: 600;
}

.task-count {
  margin-left: var(--space-2);
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 400;
}

.task-hint {
  margin: var(--space-1) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.task-text {
  max-width: 34rem;
  margin: var(--space-6) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 500;
  line-height: 1.55;
}

.recorder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  margin-top: var(--space-8);
}
.preparation { display: grid; justify-items: center; gap: var(--space-1); color: var(--ink-secondary); }
.countdown { color: var(--ink-primary); font-family: var(--font-family-data); font-size: 72px; line-height: 1.1; font-weight: 600; }
.creature-label { color: var(--ink-primary); font-size: var(--font-size-lg); }

.recorder-status {
  margin: 0;
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
}

.primary-button {
  min-height: 44px;
  padding: 0 var(--space-6);
  border: 1px solid var(--ink-primary);
  border-radius: var(--radius-md);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.primary-button:disabled {
  border-color: var(--border-default);
  background: var(--surface-subtle);
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.text-button { min-height: 44px; padding: var(--space-2) 0; border: none; background: transparent; color: var(--ink-secondary); font: inherit; font-size: var(--font-size-sm); cursor: pointer; }
.primary-button:focus-visible, .text-button:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.metric {
  padding: var(--space-3);
}

.metric + .metric {
  border: none;
}

.metric dt {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.metric dd {
  margin: var(--space-1) 0 0;
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
  font-weight: 600;
}

.reason {
  margin: var(--space-4) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.55;
}

.note {
  margin: var(--space-2) 0 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

@media (max-width: 640px) {
  .intro-actions { grid-template-columns: auto 1fr; }
  .intro-cost { grid-column: 1 / -1; grid-row: 1; }
  .intro-actions > .text-button { justify-self: end; }
  .test-intro,
  .test-task {
    padding: var(--space-6) var(--space-4);
  }

  .task-text {
    font-size: var(--font-size-lg);
  }
}

</style>
